/**
 * useSubscriptionStatus Hook
 *
 * Provides consistent subscription status and authentication state across all pages.
 * In development mode, returns the locally configured subscription tier.
 */
import { useAuth0 } from "@auth0/auth0-react";
import {
  getDevSubscriptionTier,
  resolveSubscriptionEntitlements,
} from "../utils/subscriptionEntitlements";

/**
 * Returns the current user's subscription status and authentication state.
 *
 * @returns {Object} Subscription status object
 * @returns {boolean} isPaid - Whether user has active/trialing subscription
 * @returns {boolean} isAuthenticated - Whether user is authenticated (always true in dev)
 * @returns {string} subStatus - Raw subscription status ('free', 'active', 'trialing', etc.)
 * @returns {Object|undefined} user - Auth0 user object
 */
export function useSubscriptionStatus() {
  const { user, isAuthenticated: authIsAuthenticated } = useAuth0();
  const entitlements = resolveSubscriptionEntitlements(user, {
    // Returns null outside development, leaving production claims untouched.
    devSubscriptionTier: getDevSubscriptionTier(),
  });

  const isAuthenticated = process.env.NODE_ENV === "development"
    ? true
    : authIsAuthenticated;

  return {
    isPaid: entitlements.hasCoreAccess,
    hasCoreAccess: entitlements.hasCoreAccess,
    hasResearchAccess: entitlements.hasResearchAccess,
    subscriptionTier: entitlements.subscriptionTier,
    subStatus: entitlements.subscriptionStatus,
    user,
    isAuthenticated,
  };
}

export default useSubscriptionStatus;
